import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type SimulationCreditState = {
  signedIn: boolean;
  isGuest: boolean;
  balance: number;
  /** True when the account has a live subscription, so the add-on can be bought. */
  canBuyAddon: boolean;
  addonPaise: number;
};

/** Authoritative balance for the current request. The client never supplies it. */
export const fetchSimulationCredits = createServerFn({ method: "POST" }).handler(
  async (): Promise<SimulationCreditState> => {
    const { readAuthUser } = await import("./session.server");
    const { readSimulationCredits } = await import("./simulation-credits.server");
    const { resolveAddonPlan } = await import("./subscription.server");

    const user = await readAuthUser();
    if (!user) {
      return { signedIn: false, isGuest: false, balance: 0, canBuyAddon: false, addonPaise: 2900 };
    }
    if (user.isAnonymous) {
      return { signedIn: true, isGuest: true, balance: 0, canBuyAddon: false, addonPaise: 2900 };
    }

    const [balance, addonPlan] = await Promise.all([
      readSimulationCredits(user.id),
      resolveAddonPlan(user.id),
    ]);

    return {
      signedIn: true,
      isGuest: false,
      balance,
      canBuyAddon: addonPlan !== null,
      addonPaise: addonPlan?.addonPaise ?? 2900,
    };
  },
);

/** Spends one credit when a simulation is opened. Never goes below zero. */
export const useSimulationCredit = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ simulation_id: z.string().trim().min(1).max(80) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { readAuthUser } = await import("./session.server");
    const { spendSimulationCredit, readSimulationCredits } = await import(
      "./simulation-credits.server"
    );

    const user = await readAuthUser();
    if (!user) throw new Error("NOT_AUTHENTICATED");
    if (user.isAnonymous) throw new Error("GUEST_NOT_ALLOWED");

    const result = await spendSimulationCredit({
      userId: user.id,
      simulationId: data.simulation_id,
    });
    if (!result.consumed && !result.duplicate) {
      return { allowed: false, balance: await readSimulationCredits(user.id) };
    }
    return { allowed: true, balance: result.balance };
  });
